import React from 'react';
import { cn } from '@/lib/utils';
import { Check } from 'lucide-react';

/**
 * Cells you can type into, so a grid can be the form.
 *
 * This is the piece that retires the "Complete Stage" and "Record Production"
 * dialogs. Those collect five fields — quantity done, quantity rejected,
 * operator, machine, remarks — all of which are columns already on the row you
 * clicked. Once the cells are writable there is nothing left for the dialog to
 * ask.
 *
 * The cells stay flat until focused. A grid of forty visible input boxes reads
 * as forty things somebody forgot to fill in; the same forty as plain cells read
 * as a record that is mostly written already.
 */

const BASE = 'w-full min-w-0 bg-transparent px-1 py-0.5 text-[13px] text-slate-700 ' +
  'placeholder:text-slate-300 focus:bg-blue-50 focus:outline-none focus:ring-1 ' +
  'focus:ring-inset focus:ring-brand-blue disabled:text-slate-400';

export function TextCell({ value, onChange, placeholder, disabled, align = 'left', label, className }) {
  return (
    <input
      type="text"
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      aria-label={label}
      className={cn(BASE, align === 'right' && 'text-right', align === 'center' && 'text-center', className)}
    />
  );
}

export function NumberCell({ value, onChange, placeholder, disabled, min, max, label, className }) {
  return (
    <input
      type="number"
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      min={min}
      max={max}
      aria-label={label}
      className={cn(
        BASE, 'text-right tabular-nums',
        className
      )}
    />
  );
}

export function SelectCell({ value, onChange, options = [], placeholder = '—', disabled, label, className }) {
  return (
    <select
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      disabled={disabled}
      aria-label={label}
      className={cn(BASE, 'cursor-pointer', className)}
    >
      <option value="">{placeholder}</option>
      {options.map(o => {
        const val = o.value ?? o;
        return <option key={val} value={val}>{o.label ?? o}</option>;
      })}
    </select>
  );
}

/**
 * Yes / No as the cell itself rather than a badge beside it.
 *
 * A badge pushes the row from 30px to 42px, which costs a third of the rows on
 * screen. Colouring the cell says the same thing for free.
 */
export function StateCell({ on, onToggle, onLabel = 'Yes', offLabel = 'No', disabled, label }) {
  if (disabled) {
    return (
      <span className={cn('block px-1 py-0.5 text-[12px] font-medium',
        on ? 'text-emerald-700' : 'text-slate-400')}>
        {on ? onLabel : offLabel}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onToggle(!on)}
      aria-pressed={on}
      aria-label={label}
      className={cn(
        'flex w-full items-center justify-center gap-1 px-1 py-0.5 text-[11px] font-semibold transition-colors',
        on ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'text-slate-400 hover:bg-slate-100'
      )}
    >
      {on && <Check className="h-2.5 w-2.5" />}
      {on ? onLabel : offLabel}
    </button>
  );
}

/** A read-only stamp — the machine wrote it, a person should not edit it. */
export function StampCell({ children, className }) {
  return (
    <span className={cn('block px-1 py-0.5 text-[12px] tabular-nums text-slate-500', className)}>
      {children || '—'}
    </span>
  );
}

/**
 * A small action that lives inside a row without turning it into a toolbar.
 *
 * `iconOnly` keeps the accessible name and the tooltip while dropping the
 * visible text, which is what a row of six actions needs to stay 30px tall.
 */
export function CellButton({ icon: Icon, label, iconOnly = false, onClick, tone = 'default', disabled, testId }) {
  const tones = {
    default: 'text-slate-500 hover:bg-slate-100 hover:text-slate-800',
    good: 'text-emerald-700 hover:bg-emerald-50',
    accent: 'text-brand-gold-deep hover:bg-amber-50',
    danger: 'text-red-500 hover:bg-red-50',
  };
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick?.(e); }}
      disabled={disabled}
      title={label}
      aria-label={label}
      data-testid={testId}
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-medium transition-colors disabled:opacity-40',
        tones[tone]
      )}
    >
      {Icon && <Icon className="h-3.5 w-3.5" />}
      {!iconOnly && label}
    </button>
  );
}
